import React, { useRef, useState } from 'react';
import { Button, Drawer, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import useScreenType from '../../../hooks/context/useScreenType';

/**
 * Asks before a table is deleted: printed QR codes for it stop working, and switching it off is the
 * softer way to pause it. A dialog on desktop, a bottom sheet below. Esc, × or a click outside
 * cancels, and focus goes back to the button that opened it; after a delete, `afterClose` lets the
 * list put it on the next row instead, since that button is gone.
 */
export default function DeleteTableConfirm({ table, onCancel, onConfirm, afterClose }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const { isDesktop } = useScreenType();
  const [deleting, setDeleting] = useState(false);
  const open = Boolean(table);
  // Keeps the name while the dialog animates closed.
  const shown = useRef(table);

  if (table) {
    shown.current = table;
  }

  const confirm = async () => {
    setDeleting(true);
    await onConfirm(table);
    setDeleting(false);
  };

  const title = shown.current ? t('delete.title', { name: shown.current.name }) : '';
  const body = (
    <div className="flex flex-col gap-xs text-gray-600" data-testid="table-delete-confirm">
      <p className="m-0">{t('delete.warning')}</p>
      <p className="m-0">{t('delete.softer')}</p>
    </div>
  );
  const footer = (
    <div className="flex flex-col gap-xs">
      <Button type="primary" danger block loading={deleting} onClick={confirm}>
        {t('delete.confirm')}
      </Button>
      <Button block onClick={onCancel} disabled={deleting}>
        {t('delete.cancel')}
      </Button>
    </div>
  );

  if (isDesktop) {
    return (
      <Modal
        open={open}
        title={title}
        onCancel={onCancel}
        footer={footer}
        width={480}
        afterClose={afterClose}
        destroyOnHidden
      >
        {body}
      </Modal>
    );
  }

  return (
    <Drawer
      open={open}
      title={title}
      onClose={onCancel}
      footer={footer}
      placement="bottom"
      size="auto"
      afterOpenChange={(opened) => !opened && afterClose?.()}
      destroyOnHidden
    >
      {body}
    </Drawer>
  );
}
